import type { ProjectConfig } from "@super-gantt/engine";
import type { PlanOp, PlanState } from "@super-gantt/core";

export class ApiError extends Error {
  constructor(public status: number, message: string, public body?: unknown) { super(message); }
}

/** Identifies this browser tab so live updates can skip echoes of its own writes. */
export const CLIENT_ID = crypto.randomUUID();

async function call<T>(method: string, path: string, body?: unknown): Promise<T> {
  const res = await fetch(path, {
    method,
    credentials: "include",
    headers: { "x-client-id": CLIENT_ID, ...(body !== undefined ? { "content-type": "application/json" } : {}) },
    body: body !== undefined ? JSON.stringify(body) : undefined,
  });
  const text = await res.text();
  const data = text ? JSON.parse(text) : null;
  if (!res.ok) throw new ApiError(res.status, data?.error || res.statusText, data);
  return data as T;
}

export interface ProjectSummary { id: string; name: string; revision: number; updatedAt: string }
export interface ProjectDetail {
  id: string;
  name: string;
  config: ProjectConfig;
  state: PlanState;
  revision: number;
  role: "owner" | "admin" | "member";
  organizationId: string;
}
export interface Snapshot { id: string; name: string; createdAt: string; createdBy: string | null; revision: number }
export interface Activity { revision: number; summary: string; actorName: string | null; createdAt: string }

export const api = {
  listProjects: () => call<ProjectSummary[]>("GET", "/api/projects"),
  createProject: (body: { name: string; preset: "ssx" | "blank"; startDate?: string; boardState?: unknown; organizationId?: string }) =>
    call<{ id: string }>("POST", "/api/projects", body),
  getProject: (id: string) => call<ProjectDetail>("GET", `/api/projects/${id}`),
  renameProject: (id: string, name: string) => call<{ ok: true }>("PATCH", `/api/projects/${id}`, { name }),
  deleteProject: (id: string) => call<{ ok: true }>("DELETE", `/api/projects/${id}`),
  putConfig: (id: string, config: ProjectConfig) => call<{ revision: number }>("PUT", `/api/projects/${id}/config`, { config }),
  applyOps: (id: string, baseRevision: number, ops: PlanOp[]) =>
    call<{ revision: number }>("POST", `/api/projects/${id}/ops`, { baseRevision, ops }),
  activity: (id: string) => call<Activity[]>("GET", `/api/projects/${id}/activity`),
  listSnapshots: (id: string) => call<Snapshot[]>("GET", `/api/projects/${id}/snapshots`),
  saveSnapshot: (id: string, name: string) => call<Snapshot>("POST", `/api/projects/${id}/snapshots`, { name }),
  restoreSnapshot: (id: string, sid: string) => call<{ revision: number }>("POST", `/api/projects/${id}/snapshots/${sid}/restore`),
  /** Pulls the project's linked Google Sheet and returns parsed (and profile-adjusted) backlog rows. */
  /** Parses pasted spreadsheet rows (TSV) with the project's column mapping. */
};
